/**
 * The customer database (Clients → Customer database).
 *
 * Two things live here, kept apart on purpose:
 *
 * - **Connect's customers** (`crm.customer_identity.view`, `docs/37`): one
 *   person across every client, with the cases waiting for a person and the
 *   history — tabs present only for those who hold that grant;
 * - **Client records**: the clients' own books, grouped, below — what this page
 *   always was, unchanged.
 *
 * ## Client records
 *
 * Every client's customers, one row per person.
 *
 * The server groups them — a customer kept by several clients is one row,
 * matched by mobile number (else email) — and says whether those clients
 * disagree about the name or the email. The page's job is to make those two
 * things obvious: a "Shared" count on every customer kept by more than one
 * client, a badge where the clients disagree, and, expanded, the records side
 * by side with the disagreeing values highlighted against what most clients
 * say.
 *
 * Everything is server-side: search, the filters, the sort and the pages. The
 * export takes the same parameters, so it writes exactly what is filtered —
 * every page of it, not just the one on screen.
 *
 * **Deleting** (owner, 2026-10-19; `crm.customer.delete`) removes a person from
 * every client that keeps them — these rows are the clients' own contact
 * books, read live, so nothing re-creates a deleted customer except a client
 * adding it again. One request either way: the rows ticked by their keys, or
 * every row the filters match, which the server resolves itself with the count
 * the person confirmed.
 */

import { Fragment, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronDown,
  ChevronRight,
  Download,
  RefreshCw,
  Search,
  Trash2,
  Users,
  X,
} from 'lucide-react';
import {
  api,
  ApiError,
  Badge,
  Button,
  Can,
  Card,
  Confirm,
  cn,
  EmptyState,
  ErrorState,
  formatDate,
  formatNumber,
  Input,
  LoadingState,
  PageHeader,
  Pager,
  RecordSelect,
  ScrollArea,
  Tabs,
  toast,
  useCan,
  useDebounced,
} from '@connect/web-shared';

import { ConnectCustomers, IdentityHistory, ReviewQueue } from './connect-customers';
import { identityApi, identityKeys } from './identity-api';

interface CustomerRecord {
  client_public_id: string;
  client_name: string;
  client_code: string;
  contact_public_id: string;
  name: string;
  mobile: string | null;
  email: string | null;
  company: string | null;
  city: string | null;
  can_receive: boolean;
  updated_at: string;
}

interface CustomerGroup {
  key: string;
  name: string;
  mobile: string | null;
  email: string | null;
  client_count: number;
  record_count: number;
  names_differ: boolean;
  emails_differ: boolean;
  mobiles_differ: boolean;
  updated_at: string;
  records: CustomerRecord[];
}

type View = 'all' | 'shared' | 'differs';

interface Deleted {
  customers: number;
  records: number;
  clients: number;
}
type SortField = 'name' | 'mobile' | 'clients' | 'updated_at';

const VIEWS: { id: View; label: string; hint: string }[] = [
  { id: 'all', label: 'All customers', hint: 'Everybody any client keeps' },
  { id: 'shared', label: 'Shared', hint: 'Kept by two or more clients' },
  { id: 'differs', label: 'Clients disagree', hint: 'A different name or email between clients' },
];

const same = (a: string | null, b: string | null) =>
  (a ?? '').trim().toLowerCase() === (b ?? '').trim().toLowerCase();

type Tab = 'customers' | 'review' | 'records' | 'history';

export function CustomerDatabasePage() {
  const connect = useCan('crm.customer_identity.view');
  const [params, setParams] = useSearchParams();
  const asked = params.get('tab') as Tab | null;
  const tab: Tab = connect ? (asked ?? 'customers') : 'records';
  const status = useQuery({
    queryKey: identityKeys.status,
    queryFn: identityApi.status,
    enabled: connect,
  });
  const cases = status.data?.open_reviews ?? 0;
  return (
    <div className="flex flex-1 flex-col gap-4">
      <PageHeader
        title="Customer database"
        description={
          connect
            ? 'Connect’s customers — one person across every client — and each client’s own records of them. Nothing here changes a client’s contacts.'
            : 'Every client’s customers in one place. A customer kept by several clients is one row — expand it to compare what each client holds.'
        }
        breadcrumbs={[{ label: 'Clients' }, { label: 'Customer database' }]}
        tabs={
          connect ? (
            <Tabs
              value={tab}
              onChange={(id) => setParams({ tab: id }, { replace: true })}
              items={[
                { id: 'customers', label: 'Customers' },
                {
                  id: 'review',
                  label: cases ? `Review · ${formatNumber(cases)}` : 'Review',
                },
                { id: 'records', label: 'Client records' },
                { id: 'history', label: 'History' },
              ]}
            />
          ) : undefined
        }
      />
      {tab === 'customers' ? (
        <ConnectCustomers />
      ) : tab === 'review' ? (
        <ReviewQueue />
      ) : tab === 'history' ? (
        <IdentityHistory />
      ) : (
        <ClientRecords />
      )}
    </div>
  );
}

/** The clients' own books, grouped — one row per person, by mobile, else email. */
function ClientRecords() {
  const [search, setSearch] = useState('');
  const q = useDebounced(search.trim(), 300);
  const [view, setView] = useState<View>('all');
  const [client, setClient] = useState<string | null>(null);
  const [sort, setSort] = useState<{ field: SortField; desc: boolean }>({ field: 'name', desc: false });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [exporting, setExporting] = useState(false);
  const canDelete = useCan('crm.customer.delete');
  const queryClient = useQueryClient();
  // The rows ticked, by key — or every row the filters match.
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [allMatching, setAllMatching] = useState(false);
  const [confirming, setConfirming] = useState(false);

  // A new question starts at its first page, with nothing selected: a
  // selection is of what one question showed.
  useEffect(() => {
    setPage(1);
    setPicked(new Set());
    setAllMatching(false);
  }, [q, view, client, sort, pageSize]);

  const params = {
    q: q || undefined,
    client: client ?? undefined,
    shared: view === 'shared' ? true : undefined,
    differs: view === 'differs' ? true : undefined,
    sort: `${sort.field}:${sort.desc ? 'desc' : 'asc'}`,
  };

  const customers = useQuery({
    queryKey: ['customers', 'database', params, page, pageSize],
    queryFn: () =>
      api.page<CustomerGroup>('/crm/customers', { params: { ...params, page, page_size: pageSize } }),
    placeholderData: keepPreviousData,
  });

  const toggle = (key: string) =>
    setOpen((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const sortBy = (field: SortField) =>
    setSort((current) =>
      current.field === field ? { field, desc: !current.desc } : { field, desc: field !== 'name' },
    );

  const exportCsv = async () => {
    setExporting(true);
    try {
      await api.download('/crm/customers/export', 'customers.csv', { params });
    } catch (error) {
      toast.error('Could not export', error instanceof ApiError ? error.message : undefined);
    } finally {
      setExporting(false);
    }
  };

  const rows = customers.data?.rows ?? [];
  const total = customers.data?.total ?? 0;
  const count = allMatching ? total : picked.size;
  const pageAllPicked = rows.length > 0 && rows.every((row) => picked.has(row.key));
  const clear = () => {
    setPicked(new Set());
    setAllMatching(false);
  };
  const pick = (key: string) =>
    setPicked((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const remove = useMutation({
    mutationFn: () =>
      allMatching
        ? api.post<Deleted>('/crm/customers/delete', { matching: true, expected: total }, { params })
        : api.post<Deleted>('/crm/customers/delete', { keys: [...picked] }),
    onSuccess: (done) => {
      clear();
      void queryClient.invalidateQueries({ queryKey: ['customers'] });
      toast.success(
        `${formatNumber(done.customers)} customer${done.customers === 1 ? '' : 's'} deleted`,
        `${formatNumber(done.records)} record${done.records === 1 ? '' : 's'} removed from ${formatNumber(done.clients)} client${done.clients === 1 ? '' : 's'}.`,
      );
    },
    onError: (error) => toast.error('Nothing was deleted', error instanceof ApiError ? error.message : undefined),
  });

  return (
    <div className="space-y-4">
      <Card>
        <div className="flex flex-wrap items-end gap-3 border-b border-line p-3">
          <div className="min-w-60 flex-1">
            <Input
              aria-label="Search"
              placeholder="Search by name, mobile, email or client"
              prefix={<Search className="size-4" />}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
          <div className="w-64">
            <RecordSelect
              endpoint="/crm/clients"
              value={client}
              onChange={(value) => setClient(value)}
              toOption={(row) => ({ value: String(row.public_id), label: String(row.name) })}
              placeholder="Any client"
              clearable
            />
          </div>
          <div role="radiogroup" aria-label="Show" className="flex rounded-control border border-line p-0.5">
            {VIEWS.map((item) => (
              <button
                key={item.id}
                type="button"
                role="radio"
                aria-checked={view === item.id}
                title={item.hint}
                onClick={() => setView(item.id)}
                className={cn(
                  'rounded-[calc(var(--radius-control)-2px)] px-3 py-1.5 text-xs font-medium transition-colors',
                  view === item.id ? 'bg-accent text-accent-fg' : 'text-muted hover:text-body',
                )}
              >
                {item.label}
              </button>
            ))}
          </div>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Refresh"
            onClick={() => void customers.refetch()}
          >
            <RefreshCw className={cn('size-4', customers.isFetching && 'animate-spin')} />
          </Button>
          <span className="flex-1" />
          <Can permission="crm.customer.export">
            <Button variant="secondary" onClick={() => void exportCsv()} loading={exporting} disabled={total === 0}>
              <Download className="size-4" />
              Export CSV
            </Button>
          </Can>
        </div>

        {canDelete && count > 0 ? (
          <div className="flex flex-wrap items-center gap-2 border-b border-line bg-accent-subtle px-3 py-2 text-xs text-accent">
            <span className="font-medium">
              {allMatching ? `All ${formatNumber(total)} matching customers selected` : `${formatNumber(count)} selected`}
            </span>
            {!allMatching && pageAllPicked && total > rows.length ? (
              <button type="button" className="underline underline-offset-2" onClick={() => setAllMatching(true)}>
                Select all {formatNumber(total)} matching
              </button>
            ) : null}
            <span className="flex-1" />
            <Button variant="danger" size="sm" onClick={() => setConfirming(true)}>
              <Trash2 className="size-3.5" />
              Delete
            </Button>
            <button type="button" aria-label="Clear selection" onClick={clear} className="rounded p-1 text-faint hover:text-body">
              <X className="size-4" />
            </button>
          </div>
        ) : null}

        {customers.isLoading ? (
          <LoadingState label="Loading customers" />
        ) : customers.isError ? (
          <ErrorState
            message={customers.error instanceof ApiError ? customers.error.message : undefined}
            onRetry={() => void customers.refetch()}
          />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<Users className="size-5" />}
            title={q || client || view !== 'all' ? 'No customers match' : 'No customers yet'}
            description={
              q || client || view !== 'all'
                ? 'Try another search, or show all customers.'
                : 'Customers appear here as clients add them — from the app, a phone sync or an import.'
            }
          />
        ) : (
          <ScrollArea className="">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-line bg-surface-2 text-left text-2xs uppercase tracking-wide text-faint">
                  {canDelete ? (
                    <th className="w-9 pl-3">
                      <input
                        type="checkbox"
                        aria-label="Select every customer on this page"
                        checked={allMatching || pageAllPicked}
                        onChange={() => {
                          if (allMatching || pageAllPicked) clear();
                          else setPicked(new Set(rows.map((row) => row.key)));
                        }}
                      />
                    </th>
                  ) : null}
                  <th className="w-9" />
                  <SortHeader label="Customer" field="name" sort={sort} onSort={sortBy} />
                  <SortHeader label="Mobile" field="mobile" sort={sort} onSort={sortBy} />
                  <th className="px-3 py-2 font-medium">Email</th>
                  <SortHeader label="Clients" field="clients" sort={sort} onSort={sortBy} />
                  <SortHeader label="Updated" field="updated_at" sort={sort} onSort={sortBy} />
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {rows.map((group) => {
                  const expanded = open.has(group.key);
                  const shared = group.client_count > 1;
                  return (
                    <Fragment key={group.key}>
                      <tr
                        className={cn('cursor-pointer hover:bg-surface-2', expanded && 'bg-surface-2')}
                        onClick={() => toggle(group.key)}
                      >
                        {canDelete ? (
                          <td className="pl-3" onClick={(event) => event.stopPropagation()}>
                            <input
                              type="checkbox"
                              aria-label={`Select ${group.name || group.mobile || group.email || 'this customer'}`}
                              checked={allMatching || picked.has(group.key)}
                              disabled={allMatching}
                              onChange={() => pick(group.key)}
                            />
                          </td>
                        ) : null}
                        <td className="pl-3 text-faint">
                          <button
                            type="button"
                            aria-expanded={expanded}
                            aria-label={`${expanded ? 'Hide' : 'Show'} the clients that keep ${group.name || 'this customer'}`}
                            onClick={(event) => {
                              event.stopPropagation();
                              toggle(group.key);
                            }}
                            className="rounded p-0.5 hover:bg-surface-3"
                          >
                            {expanded ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
                          </button>
                        </td>
                        <td className="px-3 py-2.5">
                          <span className="flex flex-wrap items-center gap-1.5">
                            <span className="font-medium text-heading">{group.name || '—'}</span>
                            {group.names_differ ? <Badge tone="warning">Names differ</Badge> : null}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-3 py-2.5 font-mono text-xs">{group.mobile ?? '—'}</td>
                        <td className="px-3 py-2.5">
                          <span className="flex flex-wrap items-center gap-1.5">
                            <span className="text-body">{group.email ?? <span className="text-faint">—</span>}</span>
                            {group.emails_differ ? <Badge tone="warning">Emails differ</Badge> : null}
                          </span>
                        </td>
                        <td className="px-3 py-2.5">
                          {shared ? (
                            <Badge tone="accent">Shared · {group.client_count}</Badge>
                          ) : (
                            <span className="text-muted">1</span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-3 py-2.5 text-muted">{formatDate(group.updated_at)}</td>
                      </tr>
                      {expanded ? (
                        <tr className="bg-surface-2/60">
                          <td colSpan={canDelete ? 2 : 1} />
                          <td colSpan={5} className="px-3 pb-4 pt-1">
                            <Comparison group={group} />
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </ScrollArea>
        )}

        <Pager
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </Card>

      <Confirm
        open={confirming}
        onClose={() => setConfirming(false)}
        title={`Delete ${formatNumber(count)} customer${count === 1 ? '' : 's'} from every client?`}
        message="Each client that keeps them loses its own record of them, and they leave its lists. Nothing brings them back except a client adding them again. Every record removed is in the audit log."
        confirmLabel="Delete"
        onConfirm={async () => {
          setConfirming(false);
          await remove.mutateAsync().catch(() => undefined);
        }}
      />

      <p className="text-xs text-faint">
        One row per customer, matched by mobile number (or by email where there is no number).
        {total > 0 ? ` ${formatNumber(total)} customer${total === 1 ? '' : 's'} match.` : ''}
      </p>
    </div>
  );
}

function SortHeader({
  label,
  field,
  sort,
  onSort,
}: {
  label: string;
  field: SortField;
  sort: { field: SortField; desc: boolean };
  onSort: (field: SortField) => void;
}) {
  const active = sort.field === field;
  const Icon = active ? (sort.desc ? ArrowDown : ArrowUp) : ArrowUpDown;
  return (
    <th
      className="px-3 py-2 font-medium"
      aria-sort={active ? (sort.desc ? 'descending' : 'ascending') : 'none'}
    >
      <button
        type="button"
        onClick={() => onSort(field)}
        className={cn('inline-flex items-center gap-1 uppercase hover:text-body', active && 'text-body')}
      >
        {label}
        <Icon className="size-3" />
      </button>
    </th>
  );
}

/**
 * What each client keeps for one customer, side by side. A value that differs
 * from what most clients say is highlighted — that is the disagreement the
 * badge on the row announced.
 */
function Comparison({ group }: { group: CustomerGroup }) {
  const differ = 'rounded bg-warning-subtle px-1 text-warning';
  return (
    <div className="overflow-hidden rounded-card border border-line bg-surface">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-line text-left text-2xs uppercase tracking-wide text-faint">
            <th className="px-3 py-2 font-medium">Client</th>
            <th className="px-3 py-2 font-medium">Name</th>
            <th className="px-3 py-2 font-medium">Mobile</th>
            <th className="px-3 py-2 font-medium">Email</th>
            <th className="px-3 py-2 font-medium">Company</th>
            <th className="px-3 py-2 font-medium">City</th>
            <th className="px-3 py-2 font-medium">Receives</th>
            <th className="px-3 py-2 font-medium">Updated</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          {group.records.map((record) => (
            <tr key={record.contact_public_id}>
              <td className="px-3 py-2">
                <Link
                  to={`/clients/${record.client_public_id}?tab=contacts`}
                  className="font-medium text-accent hover:underline"
                >
                  {record.client_name}
                </Link>
                <span className="ml-1.5 font-mono text-2xs text-faint">{record.client_code}</span>
              </td>
              <td className="px-3 py-2">
                <span className={cn(!same(record.name, group.name) && differ)}>{record.name || '—'}</span>
              </td>
              <td className="px-3 py-2 font-mono">
                <span className={cn(!same(record.mobile, group.mobile) && differ)}>{record.mobile ?? '—'}</span>
              </td>
              <td className="px-3 py-2">
                <span className={cn(!same(record.email, group.email) && differ)}>{record.email ?? '—'}</span>
              </td>
              <td className="px-3 py-2 text-muted">{record.company ?? '—'}</td>
              <td className="px-3 py-2 text-muted">{record.city ?? '—'}</td>
              <td className="px-3 py-2">
                {record.can_receive ? (
                  <span className="text-success">Yes</span>
                ) : (
                  <span className="text-faint">Off</span>
                )}
              </td>
              <td className="whitespace-nowrap px-3 py-2 text-muted">{formatDate(record.updated_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {group.names_differ || group.emails_differ ? (
        <p className="border-t border-line px-3 py-2 text-2xs text-faint">
          Highlighted values differ from what most clients hold for this customer.
        </p>
      ) : null}
    </div>
  );
}
